import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Sparkles } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PricingSection from '../components/PricingSection';
import { fireEvent } from '../utils/funnelEvents';

const PricingPage = () => {
  useEffect(() => {
    fireEvent('team_page_view', { vertical: 'pricing' });
  }, []);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
        { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://www.getsortio.com/pricing' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Sortio',
      description: 'AI file sorter that automatically sorts, renames, and organizes files using natural language prompts. Works on Mac, Windows, and Linux.',
      brand: { '@type': 'Brand', name: 'Sortio' },
      offers: [
        { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD', description: 'Free AI trial included, no credit card required. One-time AI trial allowance, enough to try Sortio on a real folder. Rule-based sorting free and unlimited, up to 5 rules and 3 Spaces.', availability: 'https://schema.org/InStock', url: 'https://www.getsortio.com/download' },
        { '@type': 'Offer', name: 'Pro Monthly', price: '14.99', priceCurrency: 'USD', description: 'Monthly AI allowance sized for heavy use, unlimited automations, watch folders, commercial license. Bring your own AI key: unmetered, no allowance used.', availability: 'https://schema.org/InStock', url: 'https://www.getsortio.com/download' },
        { '@type': 'Offer', name: 'Pro Annual', price: '99', priceCurrency: 'USD', description: 'Pro features billed yearly, saves about 45% versus monthly', availability: 'https://schema.org/InStock', url: 'https://www.getsortio.com/download' },
        { '@type': 'Offer', name: 'Team Monthly', price: '29', priceCurrency: 'USD', description: 'Per seat per month. Double the Pro AI allowance per seat, pooled across your team. Add seats to grow your shared allowance. Team workspaces, admin console, priority support.', availability: 'https://schema.org/InStock', url: 'https://www.getsortio.com/checkout/team' },
        { '@type': 'Offer', name: 'Team Annual', price: '290', priceCurrency: 'USD', description: 'Per seat per year, two months free versus monthly billing. Double the Pro AI allowance per seat, pooled across your team. Team workspaces, admin console, priority support.', availability: 'https://schema.org/InStock', url: 'https://www.getsortio.com/checkout/team' },
        { '@type': 'Offer', name: 'Enterprise', price: '50', priceCurrency: 'USD', description: 'Per seat per month. Volume licensing for 25+ seats, audit logs, centralized billing, custom invoicing, priority support. Contact sales.', availability: 'https://schema.org/InStock', url: 'mailto:marcus@getsortio.com' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 pt-24">
      <SEOHead
        title="Sortio Pricing: Free, Pro $14.99/mo, Team $29/seat, Enterprise"
        description="Sortio pricing plans. Free AI trial included, no credit card required. Pro at $14.99/month or $99/year (save 45%). Team at $29/seat/month or $290/seat/year. Enterprise volume licensing from $50/seat/month."
        canonical="https://www.getsortio.com/pricing"
        schema={schema}
      />

      <div className="container mx-auto px-6 max-w-6xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-12 flex flex-col items-center">
          <div className="inline-block px-4 py-1.5 mb-4 rounded-full bg-apple-600/40 backdrop-blur-sm border border-apple-500/40">
            <span className="text-sm font-medium text-apple-100 flex items-center">
              <Sparkles className="w-4 h-4 mr-2 text-sortio-primary" />
              Pricing
            </span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center max-w-3xl">
            Sortio <span className="apple-gradient-text">Pricing</span>: Free, Pro, Team, Enterprise
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-2xl">
            Start free with a one-time AI trial allowance, no credit card required. Upgrade to Pro for a monthly AI allowance sized for heavy use, plus unlimited automations and watch folders, billed monthly or annually. Team doubles the Pro allowance per seat, pooled across your team, and adds shared workspaces and an admin console. Enterprise covers volume licensing, centralized billing, and custom invoicing.
          </p>
        </div>
      </div>

      <PricingSection />
    </div>
  );
};

export default PricingPage;
